import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useGetDetailMetadataQuery, useSaveEntityMutation } from '../../../grid/api/gridApi';
import type { IDeveloperModel } from '../models/DeveloperModel';
import { extractErrorMessage } from '../../../../utils/errorUtils';
import Avatar from './comments/avatar';

type AssignTaskDialogProps = {
  open: boolean;
  taskId: number | null;
  taskName: string;
  anchorEl: HTMLElement | null;
  detailRouteId: number;
  developers: IDeveloperModel[];
  onClose: () => void;
  onAssigned: () => void;
};

export const AssignTaskDialog = ({
  open,
  taskId,
  taskName,
  anchorEl,
  detailRouteId,
  developers,
  onClose,
  onAssigned,
}: AssignTaskDialogProps) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);
  const [saveEntity, saveState] = useSaveEntityMutation();
  const {
    data: metadata,
    isLoading: metadataLoading,
    isError: metadataError,
  } = useGetDetailMetadataQuery(
    { detailFormId: detailRouteId },
    {
      skip: !open || !detailRouteId,
    }
  );

  const assigneeField = useMemo(
    () =>
      metadata?.fields?.find(
        (field) => (field.dataField || field.field || '').toLowerCase() === 'crmrelationassigned'
      ),
    [metadata]
  );
  const handleSelect = async (developer: IDeveloperModel) => {
    if (!taskId || !metadata?.contentKey || !assigneeField || saveState.isLoading) return;

    setSelectedId(developer.crmRelationId);
    setAssignmentError(null);
    try {
      const response = await saveEntity({
        Id: String(taskId),
        NodeKey: metadata.contentKey,
        Fields: [
          {
            FieldName: assigneeField.dataField || assigneeField.field,
            StringValue: String(developer.crmRelationId),
          },
        ],
      }).unwrap();
      if (response.data.Code !== 1) {
        throw new Error(response.data.Error || 'Failed to assign the task.');
      }
      onAssigned();
      onClose();
    } catch (error) {
      setSelectedId(null);
      setAssignmentError(extractErrorMessage(error, 'Failed to assign the task.'));
    }
  };

  const errorMessage = assignmentError
    ? assignmentError
    : saveState.error
      ? extractErrorMessage(saveState.error, 'Failed to assign the task.')
      : !metadataLoading && (metadataError || !metadata?.contentKey || !assigneeField)
        ? 'Task assignment is not configured.'
        : null;

  return (
    <Menu
      anchorEl={anchorEl}
      open={open && Boolean(anchorEl)}
      onClose={saveState.isLoading ? undefined : onClose}
      MenuListProps={{ dense: true }}
      slotProps={{ paper: { sx: { minWidth: 260, maxHeight: 360 } } }}
    >
      <MenuItem disabled>
        <Typography variant="body2" fontWeight={600} noWrap>
          Assign “{taskName}”
        </Typography>
      </MenuItem>
      {metadataLoading && (
        <MenuItem disabled>
          <CircularProgress size={18} sx={{ mr: 1 }} />
          <Typography variant="body2">Loading employees...</Typography>
        </MenuItem>
      )}
      {errorMessage && (
        <Box sx={{ px: 2, py: 1 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}
      {developers.map((developer) => (
        <MenuItem
          key={developer.crmRelationId}
          onClick={() => void handleSelect(developer)}
          disabled={saveState.isLoading}
        >
          <Box sx={{ mr: 1.5 }}>
            <Avatar
              className="kanban-card__avatar kanban-card__task-avatar"
              initials={developer.crmRelationInitials ?? developer.initials ?? ''}
              relationId={developer.crmRelationId}
              hasProfilePhoto={developer.hasProfilePhoto}
              colorKey={developer.crmRelationName}
              profilePhotoUrl={developer.crmRelationProfilePhotoUrl ?? developer.profilePhotoUrl}
            />
          </Box>
          <ListItemText primary={developer.crmRelationName} />
          {selectedId === developer.crmRelationId && <CircularProgress size={20} />}
        </MenuItem>
      ))}
    </Menu>
  );
};
